/**
 * Defensive wrapper around window.localStorage.
 *
 * localStorage is not always available or safe to touch:
 *   - SSR / prerender: `window` is undefined.
 *   - Some Android WebViews: `window.localStorage` is `null`.
 *   - Privacy/incognito modes, blocked site-data, Safari ITP: the property getter
 *     itself throws a `SecurityError` ("Access is denied for this document").
 *
 * Accessing it directly crashed the newsletter gate on `/` for a slice of mobile
 * and privacy-conscious users (Sentry MILKY-WAY-PLANNER-1N/1M/1K/18/19). This
 * wrapper degrades gracefully: reads return `null`, writes/removes are no-ops
 * when storage is unavailable, so callers behave as if nothing was persisted.
 */

let cachedStorage: Storage | null | undefined;

/**
 * Resolve a usable Storage object, or null if storage is unavailable.
 *
 * We deliberately do NOT write-probe. Merely *referencing* `window.localStorage`
 * is what throws the SecurityError in locked-down browsers, so a try/catch around
 * the reference is enough to detect the "storage is off" case. A write-probe would
 * over-reject: a browser that allows reads but blocks writes (quota full, or
 * write-restricted contexts) would get all reads disabled too, hiding persisted
 * newsletter/auth state that is still perfectly readable. Runtime read/write
 * failures on the cached reference are caught per-method below instead.
 */
function getStorage(): Storage | null {
  if (cachedStorage !== undefined) return cachedStorage;

  try {
    // Accessing the getter here is what can throw; `!window.localStorage` also
    // covers Android WebViews where it is present but null.
    cachedStorage = typeof window === 'undefined' || !window.localStorage ? null : window.localStorage;
  } catch {
    cachedStorage = null;
  }
  return cachedStorage;
}

export const safeStorage = {
  getItem(key: string): string | null {
    const storage = getStorage();
    if (!storage) return null;
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.setItem(key, value);
    } catch {
      /* quota exceeded or storage revoked mid-session — ignore */
    }
  },

  removeItem(key: string): void {
    const storage = getStorage();
    if (!storage) return;
    try {
      storage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

/** Test-only: reset the cached availability probe between test cases. */
export function __resetSafeStorageForTests(): void {
  cachedStorage = undefined;
}
